import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import api from "../lib/api";
import { Page, Field, StatusPill } from "../components/Primitives";
import { toast } from "sonner";
import { Plus, X, ScanLine, AlertTriangle, Package as PackageIcon } from "lucide-react";

const empty = {
  sku: "", name: "", category: "", unit: "pcs", qty: 0, min_qty: 5,
  cost_price: 0, sell_price: 0, gst_rate: 18, bin_location: "", barcode: "",
};

export default function Stores() {
  const [params, setParams] = useSearchParams();
  const lowOnly = params.get("low") === "1";
  const openScan = params.get("scan") === "1";
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const scannerRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/stores/items", { params: { q: q || undefined, low_only: lowOnly } });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [lowOnly]);

  useEffect(() => {
    api.get("/settings/categories", { params: { scope: "store" } }).then(({ data }) => setCategories(data));
  }, []);

  useEffect(() => { if (openScan) startScan(); /* eslint-disable-next-line */ }, [openScan]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, qty: +form.qty, min_qty: +form.min_qty, cost_price: +form.cost_price, sell_price: +form.sell_price, gst_rate: +form.gst_rate };
      await api.post("/stores/items", payload);
      toast.success("Item added"); setOpen(false); setForm(empty); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const startScan = async () => {
    setScanResult(null);
    setScanning(true);
    setTimeout(async () => {
      try {
        const id = "kk-scanner";
        const el = document.getElementById(id);
        if (!el) return;
        scannerRef.current = new Html5Qrcode(id);
        await scannerRef.current.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: 250 },
          async (text) => {
            try {
              const fd = new FormData(); fd.append("code", text);
              const { data } = await api.post("/stores/scan", fd);
              setScanResult(data);
              await scannerRef.current.stop();
              setScanning(false);
              setParams({});
            } catch {
              toast.error("Code not found: " + text);
            }
          },
          () => {}
        );
      } catch (e) {
        toast.error("Camera unavailable — enter code manually.");
        setScanning(false);
      }
    }, 100);
  };

  const stopScan = async () => {
    try { await scannerRef.current?.stop(); } catch {}
    setScanning(false);
    setParams({});
  };

  return (
    <Page
      number="05"
      title="Stores Inventory"
      subtitle="Live stock · QR / barcode scan · minimum-level alerts."
      right={
        <div className="flex gap-2">
          <button className="kk-btn-ghost" onClick={startScan} data-testid="open-scanner"><ScanLine size={14}/> Scan</button>
          <button className="kk-btn-primary" onClick={() => setOpen(true)} data-testid="add-item"><Plus size={14}/> Add Item</button>
        </div>
      }
    >
      <div className="flex gap-2 items-center mb-4 flex-wrap">
        <button onClick={() => setParams({})} className={"kk-pill " + (!lowOnly ? "kk-pill-black" : "kk-pill-muted")} data-testid="filter-all">All</button>
        <button onClick={() => setParams({ low: "1" })} className={"kk-pill " + (lowOnly ? "kk-pill-black" : "kk-pill-muted")} data-testid="filter-low"><AlertTriangle size={11} className="inline mr-1"/> Low Stock</button>
        <div className="flex-1" />
        <input className="kk-input max-w-xs" placeholder="Search name / sku / barcode" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="item-search" />
      </div>

      <div className="overflow-x-auto">
        <table className="kk-table">
          <thead>
            <tr>
              <th>SKU</th><th>Name</th><th>Category</th><th>Qty</th><th>Min</th><th>Cost</th><th>Sell</th><th>Bin</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => {
              const low = (i.qty || 0) <= (i.min_qty || 0);
              return (
                <tr key={i.id} data-testid={`item-row-${i.id}`}>
                  <td className="font-mono">{i.sku}</td>
                  <td><div className="font-semibold">{i.name}</div><div className="text-xs font-mono text-[var(--kk-muted)]">{i.barcode}</div></td>
                  <td>{i.category}</td>
                  <td className={"font-mono " + (low ? "text-[var(--kk-danger)] font-bold" : "")}>{i.qty}</td>
                  <td className="font-mono">{i.min_qty}</td>
                  <td className="font-mono">₹{i.cost_price}</td>
                  <td className="font-mono">₹{i.sell_price}</td>
                  <td>{i.bin_location}</td>
                  <td>{low ? <StatusPill status="low" /> : <span className="kk-pill kk-pill-lime">OK</span>}</td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-[var(--kk-muted)]">No items.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Scanner overlay */}
      {scanning && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-[var(--kk-surface)] p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display uppercase">Scan Item</div>
              <button onClick={stopScan} data-testid="stop-scan"><X /></button>
            </div>
            <div id="kk-scanner" className="relative w-full aspect-square bg-black" />
            <p className="text-xs text-[var(--kk-muted)] mt-3">Point camera at QR / barcode on the item.</p>
          </div>
        </div>
      )}

      {/* Scan result modal */}
      {scanResult && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setScanResult(null)}>
          <div className="bg-[var(--kk-surface)] max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="kk-overline">Item found</div>
            <div className="font-display text-2xl uppercase mt-1">{scanResult.name}</div>
            <div className="text-xs font-mono text-[var(--kk-muted)]">{scanResult.sku} · {scanResult.barcode}</div>
            <dl className="mt-4 text-sm divide-y divide-[var(--kk-border)] border-t border-b border-[var(--kk-border)]">
              <div className="flex justify-between py-2"><dt>Qty in stock</dt><dd className="font-mono">{scanResult.qty}</dd></div>
              <div className="flex justify-between py-2"><dt>Min level</dt><dd className="font-mono">{scanResult.min_qty}</dd></div>
              <div className="flex justify-between py-2"><dt>Bin</dt><dd>{scanResult.bin_location}</dd></div>
              <div className="flex justify-between py-2"><dt>Sell price</dt><dd className="font-mono">₹{scanResult.sell_price}</dd></div>
            </dl>
            <button className="kk-btn-primary mt-5 w-full justify-center" onClick={() => setScanResult(null)}>Close</button>
          </div>
        </div>
      )}

      {/* Add item */}
      {open && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={() => setOpen(false)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[var(--kk-surface)] p-8 overflow-y-auto">
            <div className="flex items-center justify-between"><h2 className="font-display text-2xl uppercase">Add Item</h2><button type="button" onClick={() => setOpen(false)}><X /></button></div>
            <div className="grid grid-cols-2 gap-5 mt-5">
              <Field label="SKU *"><input className="kk-input" required value={form.sku} onChange={upd("sku")} /></Field>
              <Field label="Barcode"><input className="kk-input" value={form.barcode} onChange={upd("barcode")} /></Field>
              <div className="col-span-2"><Field label="Name *"><input className="kk-input" required value={form.name} onChange={upd("name")} /></Field></div>
              <Field label="Category">
                <select className="kk-input" value={form.category} onChange={upd("category")} data-testid="store-cat-select">
                  <option value="">—</option>
                  {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
                <div className="text-[0.65rem] text-[var(--kk-muted)] mt-1">
                  Manage list in <a href="/settings" className="underline">Settings</a>.
                </div>
              </Field>
              <Field label="Unit"><input className="kk-input" value={form.unit} onChange={upd("unit")} /></Field>
              <Field label="Qty"><input className="kk-input" type="number" value={form.qty} onChange={upd("qty")} /></Field>
              <Field label="Min Qty"><input className="kk-input" type="number" value={form.min_qty} onChange={upd("min_qty")} /></Field>
              <Field label="Cost Price"><input className="kk-input" type="number" value={form.cost_price} onChange={upd("cost_price")} /></Field>
              <Field label="Sell Price"><input className="kk-input" type="number" value={form.sell_price} onChange={upd("sell_price")} /></Field>
              <Field label="GST %"><input className="kk-input" type="number" value={form.gst_rate} onChange={upd("gst_rate")} /></Field>
              <Field label="Bin Location"><input className="kk-input" value={form.bin_location} onChange={upd("bin_location")} /></Field>
            </div>
            <button className="kk-btn-primary mt-6" data-testid="save-item">Save Item</button>
          </form>
        </div>
      )}
    </Page>
  );
}
